// Where Otto appears on the call: the 3D Otto in video mode, the 2D Otto card in
// voice-only mode. The 2D card is also the poster while the 3D Otto loads and
// the fallback when WebGL fails, the context is lost, or the frame rate stays
// under 20 for 5 s (design 7.4, 7.5). Voice-only mode never imports the 3D
// chunk, so three.js is not loaded.
import { Component, Suspense, lazy, useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { OttoCard2D } from "../avatar/OttoCard2D";
import { loadOttoAvatar } from "../avatar/loadAvatar";
import type { AvatarFallbackReason, AvatarStats, OttoPlayerLink } from "../avatar/OttoAvatar";
import type { ModelVariant } from "../avatar/models";
import type { Mood } from "../avatar/avatarMotion";
import type { OttoState, OttoStateSnapshot } from "./ottoState";

const OttoAvatar = lazy(loadOttoAvatar);

class AvatarBoundary extends Component<{ onError: () => void; children: ReactNode }, { failed: boolean }> {
  override state = { failed: false };
  static getDerivedStateFromError() {
    return { failed: true };
  }
  override componentDidCatch(error: unknown) {
    // Loud in the console; the owner keeps talking with the 2D Otto.
    console.error("[otto] 3D Otto failed; showing the 2D Otto", error);
    this.props.onError();
  }
  override render() {
    return this.state.failed ? null : this.props.children;
  }
}

export function webglAvailable(): boolean {
  if (typeof document === "undefined") return false;
  try {
    const canvas = document.createElement("canvas");
    return Boolean(canvas.getContext("webgl2") ?? canvas.getContext("webgl"));
  } catch {
    return false;
  }
}

export interface OttoStageProps {
  mode: "video" | "voice";
  ottoState: OttoState;
  stateRef: { current: OttoStateSnapshot };
  player: OttoPlayerLink;
  variant: ModelVariant;
  statusLabel: string;
  statsRef: { current: AvatarStats & { loadMs: number | null; fellBack: boolean } };
  onFallback?: (reason: AvatarFallbackReason) => void;
  /** A held expression cue (stage cues later; the dev review sets it for the still sheet). */
  moodRef?: { current: Mood };
  /** Shown under the 2D card's status line (voice only). */
  cardNotes?: ReactNode;
  /** Changing it after a fallback tries the 3D Otto again (the owner asked for video, Oct 14 2026). */
  retryKey?: number;
}

export function OttoStage({
  mode,
  ottoState,
  stateRef,
  player,
  variant,
  statusLabel,
  statsRef,
  onFallback,
  moodRef,
  cardNotes,
  retryKey = 0,
}: OttoStageProps) {
  const [ready3d, setReady3d] = useState(false);
  const [fallenBack, setFallenBack] = useState(() => !webglAvailable());
  const mountedAt = useRef<number | null>(null);
  const reducedMotion = useMemo(
    () => typeof window !== "undefined" && Boolean(window.matchMedia?.("(prefers-reduced-motion: reduce)").matches),
    [],
  );
  const show3d = mode === "video" && !fallenBack;

  // A new retry key after a fallback: try the 3D Otto again (if the browser can draw it at all).
  const lastRetry = useRef(retryKey);
  useEffect(() => {
    if (retryKey === lastRetry.current) return;
    lastRetry.current = retryKey;
    if (webglAvailable()) {
      statsRef.current.fellBack = false;
      setFallenBack(false);
    }
  }, [retryKey, statsRef]);

  useEffect(() => {
    if (show3d && mountedAt.current === null) mountedAt.current = performance.now();
    if (!show3d) setReady3d(false);
  }, [show3d]);

  const fallBack = useCallback(
    (reason: AvatarFallbackReason) => {
      statsRef.current.fellBack = true;
      if (reason === "context_lost") statsRef.current.contextLost = true;
      setFallenBack(true);
      onFallback?.(reason);
    },
    [onFallback, statsRef],
  );

  const onReady = useCallback(() => {
    if (mountedAt.current !== null && statsRef.current.loadMs === null)
      statsRef.current.loadMs = Math.round(performance.now() - mountedAt.current);
    setReady3d(true);
  }, [statsRef]);

  return (
    <div className={`consult-otto-stage is-${mode}${show3d && ready3d ? " has-3d" : ""}`}>
      {/* The 2D card: voice-only Otto, the poster while the 3D Otto loads, and the fallback. */}
      {!(show3d && ready3d) && (
        <div className="consult-otto-stage__card">
          <OttoCard2D
            state={ottoState}
            levelRef={player.levelRef}
            statusLabel={mode === "voice" || fallenBack ? statusLabel : null}
            reducedMotion={reducedMotion}
          >
            {cardNotes}
          </OttoCard2D>
        </div>
      )}
      {show3d && (
        <div className="consult-call-avatar" aria-hidden={!ready3d} key={retryKey}>
          <AvatarBoundary onError={() => fallBack("webgl_failed")}>
            <Suspense fallback={null}>
              <OttoAvatar
                player={player}
                stateRef={stateRef}
                variant={variant}
                statsRef={statsRef}
                onReady={onReady}
                onFallback={fallBack}
                {...(moodRef ? { moodRef } : {})}
              />
            </Suspense>
          </AvatarBoundary>
        </div>
      )}
    </div>
  );
}
